import React, { useState, useEffect, useMemo } from 'react';
import { Layout as AntLayout, Menu, Dropdown, Avatar, Space, message, Select } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { UserOutlined, LogoutOutlined, SwapOutlined } from '@ant-design/icons';
import { useAuth } from '../AuthProvider';
import { getMenuItems, findMenuState } from './menuConfig';
import { getShops, switchShop } from '../../api/admin';
import './index.css';

const { Header, Content, Sider } = AntLayout;

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();
  const [shops, setShops] = useState([]);
  const [currentShopId, setCurrentShopId] = useState(user?.shopId || null);
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);

  // 平台管理员（shop_id=1）加载商户列表
  useEffect(() => {
    if (user?.is_super === 1 && user?.adminShopId === 1) {
      getShops({ pageSize: 100 }).then(res => {
        const list = res.list || res || [];
        setShops(list);
      }).catch(() => {});
    }
  }, [user]);

  const handleSwitchShop = async (shopId) => {
    try {
      const res = await switchShop(shopId);
      // 更新 token
      localStorage.setItem('admin_token', res.token);
      setCurrentShopId(shopId);
      message.success(`已切换到: ${res.shopName}`);
      // 刷新页面以重载数据
      window.location.reload();
    } catch (e) {
      message.error('切换失败');
    }
  };

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  const handleLogout = () => {
    logout();
    message.success('已登出');
  };

  // 使用 menu 属性，而不是 overlay
  const dropdownMenu = {
    items: [
      {
        key: 'profile',
        label: '个人信息',
        onClick: () => navigate('/profile'),
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        label: '退出登录',
        icon: <LogoutOutlined />,
        onClick: handleLogout,
      },
    ],
  };

  const menuItems = useMemo(() => getMenuItems(hasPermission, user), [hasPermission, user]);
  const { selectedKey, openKeys: matchedOpenKeys } = useMemo(
    () => findMenuState(menuItems, location.pathname),
    [menuItems, location.pathname]
  );

  useEffect(() => {
    if (!collapsed) {
      setOpenKeys(matchedOpenKeys);
    }
  }, [collapsed, matchedOpenKeys]);

  return (
    <AntLayout className="app-shell">
      <Sider
        className="app-shell-sider"
        collapsible
        width={220}
        collapsedWidth={72}
        collapsed={collapsed}
        onCollapse={setCollapsed}
      >
        <div className={`app-shell-logo${collapsed ? ' is-collapsed' : ''}`}>
          <div className="app-shell-logo__mark">云</div>
          {!collapsed && (
            <div className="app-shell-logo__copy">
              <div className="app-shell-logo__title">云送水</div>
              <div className="app-shell-logo__subtitle">运营后台</div>
            </div>
          )}
        </div>
        <Menu
          className="app-shell-menu"
          theme="dark"
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          openKeys={collapsed ? [] : openKeys}
          items={menuItems}
          onClick={handleMenuClick}
          onOpenChange={setOpenKeys}
        />
      </Sider>
      <AntLayout className="app-shell-main">
        <Header className="app-shell-header">
          {user?.is_super === 1 && user?.adminShopId === 1 && shops.length > 0 && (
            <Space className="app-shell-header__switcher">
              <SwapOutlined />
              <Select
                value={currentShopId}
                onChange={handleSwitchShop}
                style={{ width: 154 }}
                size="small"
                placeholder="切换商户"
              >
                {shops.map(s => (
                  <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
                ))}
              </Select>
            </Space>
          )}
          <Space className="app-shell-header__profile">
            <Dropdown menu={dropdownMenu} placement="bottomRight">
              <Avatar icon={<UserOutlined />} style={{ cursor: 'pointer' }} />
            </Dropdown>
            <span>{user?.name || user?.phone}</span>
          </Space>
        </Header>
        <Content className="app-shell-content">
          <div className="app-shell-content__inner">
            <Outlet />
          </div>
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;